

















































































































































































































































































import moment from 'moment';
import { mapActions } from 'vuex';
import {
  WorkflowFormSubmissionComment,
  IWorkflowFormFieldSubmission,
  IWorkflowFormSubmissionComment,
  IUser,
  IWorkflowFormSubmissionCommentAttachment,
  WorkflowFormSubmissionCommentAttachment
} from '@/models';
import ConfirmationDialog from '../common/ConfirmationDialog.vue';
import { ErrorManager, ISystemError } from '@/models/error';
import { cloneDeep } from 'lodash';
import auth from '@/mixins/auth';
import {
  createFile,
  diffDeep,
  getInitials,
  isHtmlTextEmpty,
  setErrorFromAxiosError
} from '@/utils/helpers';
import { PropType } from 'vue';
import VNodes from '../computation/VNodes.vue';
import MentionProvider from '../mention/MentionProvider.vue';
import QuillEditor from '../common/QuillEditor.vue';
import { get } from 'vuex-pathify';
import { AxiosError } from 'axios';
import DeleteConfirmationDialog from '../common/DeleteConfirmationDialog.vue';
import WorkflowFormSubmissionCommentAttachmentList from './WorkflowFormSubmissionCommentAttachmentList.vue';
import mixins from '@/utils/mixins';
import snackbar from '@/mixins/snackbar';

export default mixins(auth, snackbar).extend({
  components: {
    ConfirmationDialog,
    DeleteConfirmationDialog,
    VNodes,
    MentionProvider,
    QuillEditor,
    WorkflowFormSubmissionCommentAttachmentList
  },

  props: {
    workflowFormSubmission: {
      type: Object as PropType<IWorkflowFormFieldSubmission>,
      required: true
    },

    workflowFormSubmissionComment: {
      type: Object as () => IWorkflowFormSubmissionComment,
      required: true
    }
  },

  name: 'CommentListItem',

  data() {
    return {
      loading: false,
      isEditing: false,

      commentLoading: false,
      commentErrorMessage: '',
      commentDetailErrorMessage: '',

      // delete comment
      deleteCommentErrorMessage: '',
      commentDeleteInProgress: false,
      selectedComment: cloneDeep(this.workflowFormSubmissionComment),
      deleteCommentDialog: false,

      commentTextHtml: this.workflowFormSubmissionComment.getTextHtml(),
      mentionsLoading: false,

      deleteDialog: false,
      isAttachmentDeleteInProgress: false,
      attachmentDeleteError: null as ISystemError | null,
      selectedAttachment: null as null | IWorkflowFormSubmissionCommentAttachment,
      attachments: [] as IWorkflowFormSubmissionCommentAttachment[]
    };
  },

  computed: {
    mentionableUsers: get<IUser[]>(
      'workflowForm/getWorkflowFormSubmissionMentionableUsers'
    ),

    isCommentOwner(): boolean {
      return (
        this.getCurrentUserFullName === this.workflowFormSubmissionComment.owner
      );
    }
  },

  methods: {
    getFileName(name: string) {
      return name.split('/').pop()!;
    },

    openAttachmentDeleteDialog(
      attachment: IWorkflowFormSubmissionCommentAttachment
    ) {
      this.clearAllErrors();
      this.selectedAttachment = attachment;
      this.$nextTick(() => {
        this.deleteDialog = true;
      });
    },

    ...mapActions({
      updateWorkflowFormSubmissionComment:
        'workflowForm/updateWorkflowFormSubmissionComment',
      deleteWorkflowFormSubmissionComment:
        'workflowForm/deleteWorkflowFormSubmissionComment',
      removeWorkflowFormSubmissionCommentAttachment:
        'workflowForm/removeWorkflowFormSubmissionCommentAttachment',
      fetchWorkflowFormSubmissionCommentsMentionableUsers:
        'workflowForm/fetchWorkflowFormSubmissionCommentsMentionableUsers',
      addWorkflowFormSubmissionCommentAttachments:
        'workflowForm/addWorkflowFormSubmissionCommentAttachments'
    }),

    getInitials,

    timeFromNow: (date: Date | string) => {
      return moment(date).fromNow();
    },

    textHasDifference(): boolean {
      return (
        this.commentTextHtml ===
        this.workflowFormSubmissionComment.getTextHtml()
      );
    },

    canSaveComment(): boolean {
      return (
        !isHtmlTextEmpty(this.selectedComment.text) && !this.commentLoading
      );
    },

    toggleEditingComment() {
      if (!this.isEditing) {
        // To reset the selected workflowFormSubmission comment data to the original when rediting
        this.selectedComment = cloneDeep(this.workflowFormSubmissionComment);
        this.commentTextHtml = this.workflowFormSubmissionComment.getTextHtml();
      }
      this.isEditing = !this.isEditing;
    },

    stopEditingComment() {
      if (!this.selectedComment.text || this.textHasDifference()) {
        this.isEditing = false;
      }
    },

    clearAllErrors(): void {
      this.deleteCommentErrorMessage = '';
      this.commentErrorMessage = '';
      this.commentDetailErrorMessage = '';
      this.attachmentDeleteError = null;
    },

    cancelCommentDeleteDialog() {
      this.deleteCommentDialog = false;
    },

    addBaseAttachments(files: File[]) {
      this.attachments.push(
        ...files.map(
          file => new WorkflowFormSubmissionCommentAttachment({ file: file })
        )
      );
    },

    async loadMentionableUsers() {
      this.clearAllErrors();

      this.mentionsLoading = true;
      try {
        await this.fetchWorkflowFormSubmissionCommentsMentionableUsers(
          this.workflowFormSubmission.id
        );
      } catch (error) {
        this.commentErrorMessage = ErrorManager.extractApiError(
          error as AxiosError
        );
      } finally {
        this.mentionsLoading = false;
      }
    },

    cancelDeleteDialog() {
      this.deleteDialog = false;
    },

    removeAttachment(attachment: IWorkflowFormSubmissionCommentAttachment) {
      this.clearAllErrors();
      this.isAttachmentDeleteInProgress = true;

      this.removeWorkflowFormSubmissionCommentAttachment({
        submissionId: this.workflowFormSubmission.id,
        commentId: this.workflowFormSubmissionComment.id,
        attachmentId: attachment.id
      })
        .then(() => {
          this.deleteDialog = false;
        })
        .catch(error => {
          this.attachmentDeleteError = setErrorFromAxiosError(error);
        })
        .finally(() => {
          this.isAttachmentDeleteInProgress = false;
        });
    },

    removeComment() {
      this.clearAllErrors();
      this.commentDeleteInProgress = true;
      this.deleteWorkflowFormSubmissionComment({
        submissionId: this.workflowFormSubmission.id,
        commentId: this.workflowFormSubmissionComment.id
      })
        .then(() => {
          this.deleteCommentDialog = false;
        })
        .catch(error => {
          this.deleteCommentErrorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.commentDeleteInProgress = false;
        });
    },

    async editComment() {
      this.clearAllErrors();

      try {
        const parsedCommentMentionText = WorkflowFormSubmissionComment.parseHtmlToMentionText(
          this.commentTextHtml
        );

        this.selectedComment.text = parsedCommentMentionText;

        const commentDelta = diffDeep(
          this.selectedComment,
          this.workflowFormSubmissionComment,
          true
        );

        this.commentLoading = true;
        await this.updateWorkflowFormSubmissionComment({
          submissionId: this.workflowFormSubmission.id,
          comment: commentDelta
        });

        if (this.attachments.length !== 0) {
          await this.addWorkflowFormSubmissionCommentAttachments({
            submissionId: this.workflowFormSubmission.id,
            commentId: commentDelta.id,
            attachments: this.attachments
          });
          this.attachments = [];
        }
        this.isEditing = false;
      } catch (err) {
        const error = err as AxiosError;
        if (error.response) {
          if (error.response.data.detail) {
            this.commentDetailErrorMessage = error.response.data.detail;
          }
        }
        this.commentErrorMessage = ErrorManager.extractApiError(error);
      } finally {
        this.commentLoading = false;
      }
    },

    downloadAttachmentFile(
      attachment: IWorkflowFormSubmissionCommentAttachment
    ) {
      this.clearAllErrors();
      this.$services.fileLoader
        .downloadFile(attachment.file as string)
        .then(data => {
          createFile(data, attachment.filename);

          this.createSnack({
            color: 'success darken-1',
            type: 'success',
            right: true,
            useBtn: false,
            msg: `File attachment download successfully`,
            btnText: 'Ok',
            timeout: 6000
          });
        })
        .catch(error => {
          const err = setErrorFromAxiosError(error);
          this.createSnack({
            color: 'error',
            type: 'error',
            right: true,
            useBtn: false,
            msg: `Failed to download Attachment: ${err.detail || err.message}`,
            btnText: 'Close'
          });
        });
    }
  }
});
